import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatSlideToggleChange} from "@angular/material/slide-toggle";
import {ReasonComponent} from "../../../../@shared/modals/reason/reason.component";
import {filter, finalize, switchMap, takeUntil, tap} from "rxjs/operators";
import {UserService} from "../../../../@shared/services/user.service";
import {MatDialog} from "@angular/material/dialog";
import {UserXpHistoryService} from "../services/user-xp-history.service";
import {SnackBarService} from "../../../../@core/services/snack-bar.service";
import {Observable, Subject} from "rxjs";
import {IUser} from "../../../../@core/interfaces/user/user.interface";
import {WithdrawLimitModalComponent} from "./modals/withdraw-limit-modal/withdraw-limit-modal.component";
import {PayoutLockSettingsComponent} from "./modals/payout-lock-settings/payout-lock-settings.component";
import {LockModalComponent, LockModalResult} from "./modals/lock-modal/lock-modal.component";
import {LockHistoryModalComponent} from "./modals/lock-history-modal/lock-history-modal.component";
import {ConfirmComponent} from "../../../../@shared/modals/confirm/confirm.component";
import {
  IUserLock,
  LOCK_TYPE_META,
  LOCK_TYPE_ORDER,
  LockTypeEnum,
} from "../../../../@core/interfaces/user/user-lock.interface";

// Вью-модель строки лока на странице ограничений.
interface LockRow {
  type: LockTypeEnum;
  label: string;
  description: string;
  isActive: boolean;
  entry: IUserLock | null;
}

@Component({
  selector: 'app-restrictions',
  templateUrl: './restrictions.component.html',
  styleUrls: ['./restrictions.component.scss']
})
export class RestrictionsComponent implements OnInit, OnDestroy {
  public user$: Observable<IUser | null> = this.userService.user$;
  public checkUserAccess$: Observable<any> = this.userService.checkUserSteam();

  // Все типы блокировок строками (активные подсвечены); «кто/источник/время» — из самой записи лока.
  public lockRows: LockRow[] = [];
  public isRotatingProvablyFairSeed: boolean = false;

  private readonly destroyed$: Subject<void> = new Subject<void>();

  /** Открыть журнал блокировок отдельной модалкой (с серверной пагинацией) — а не грузить на странице. */
  public openHistory(): void {
    this.dialog.open(LockHistoryModalComponent, {maxWidth: '90vw'});
  }

  constructor(private userService: UserService,
              private dialog: MatDialog,
              private sb: SnackBarService,
              private userXpHistoryService: UserXpHistoryService) {
  }

  ngOnInit(): void {
    this.user$
      .pipe(
        filter((user): user is IUser => !!user?.fastId),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => this.loadLocks());
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public refresh(): void {
    this.loadLocks();
  }

  private loadLocks(): void {
    this.userService.getUserLocks()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => this.lockRows = this.buildRows(res?.locks ?? []));
    // Историю НЕ грузим здесь — она по требованию (по кнопке), чтобы не дёргать при каждом изменении локов.
  }

  private buildRows(locks: IUserLock[]): LockRow[] {
    const byType = new Map<LockTypeEnum, IUserLock>(
      locks.map(lock => [lock.type, lock]),
    );

    return LOCK_TYPE_ORDER.map(type => {
      const entry = byType.get(type) ?? null;
      return {
        type,
        label: LOCK_TYPE_META[type].label,
        description: LOCK_TYPE_META[type].description,
        isActive: !!entry,
        entry,
      };
    });
  }

  /**
   * Переключение любого типа блокировки через единый `updateUserLocks([{ type, isActive, reason }])`.
   * Легаси-колонки синхронизирует сам сервис user-lock — клиент о них не знает.
   */
  public toggleLock(row: LockRow, event: MatSlideToggleChange): void {
    this.dialog.open(LockModalComponent, {data: {isLocking: event.checked}}).afterClosed()
      .pipe(
        tap((result: LockModalResult | false) => {
          if (!result) {
            event.source.checked = !event.checked; // отменили — вернуть тоггл в исходное
          }
        }),
        filter((result): result is LockModalResult => !!result),
        switchMap((result) =>
          this.userService.toggleUserLock(row.type, event.checked, result.reason, result.expiresAt)),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.userService.requestUser();
        this.loadLocks();
      });
  }

  // Подпись «кто поставил» по записи лока/истории.
  public formatActor(actorType: string | null, actorId: number | null): string {
    if (actorType === 'admin') {
      return actorId ? `Админ #${actorId}` : 'Админ';
    }
    if (actorType === 'auto') {
      return 'Автоблокировка';
    }
    if (actorType === 'self') {
      return 'Пользователь сам';
    }
    return 'Система';
  }

  // Читаемый тип инициатора (отдельно от «кто» — для явного отображения actorType в строке лока).
  public actorTypeLabel(actorType: string | null): string {
    switch (actorType) {
      case 'admin':
        return 'Админ';
      case 'auto':
        return 'Авто';
      case 'self':
        return 'Сам пользователь';
      default:
        return 'Система';
    }
  }

  public unlockBalance(): void {
    this.userService.unlockBalance().subscribe((response) => {
      if (response.success) {
        this.sb.openSnackBar('Баланс успешно разблокирован', 3000, 'success');
      } else {
        this.sb.openSnackBar('Ошибка разблокировки баланса', 3000, 'error');
      }
    });
  }

  public toggleSetPromoMoreDeposit(event: MatSlideToggleChange): void {
    this.dialog.open(ReasonComponent).afterClosed()
      .pipe(
        tap((reason) => {
          if (!reason) {
            event.source.checked = !event.checked;
          }
        }),
        filter((reason) => reason),
        switchMap((reason) => this.userService.toggleSetPromoMoreDeposit(event.checked, reason)))
      .subscribe(() => this.userService.requestUser());
  }

  public toggleTrader(event: MatSlideToggleChange): void {
    this.userService
      .toggleTrader(event.checked)
      .subscribe(() => this.userService.requestUser());
  }

  public toggleIgnoreFraud(event: MatSlideToggleChange): void {
    this.userService
      .toggleIgnoreFraud(event.checked)
      .subscribe(() => this.userService.requestUser());
  }

  public setStoreWithdrawLimit(): void {
    this.dialog.open(WithdrawLimitModalComponent).afterClosed()
      .pipe(
        filter((withDrawLimitForm: { withdrawLimit: number, reason: string }) => !!withDrawLimitForm),
        switchMap((withDrawLimitForm: {
          withdrawLimit: number,
          reason: string
        }) => this.userXpHistoryService.setStoreWithdrawLimit(withDrawLimitForm)))
      .subscribe(() => this.userService.requestUser());
  }

  public editPayoutLock(user: IUser): void {
    this.dialog.open(PayoutLockSettingsComponent, {data: user}).afterClosed()
      .pipe(filter(v => !!v))
      .subscribe(() => this.userService.requestUser());
  }

  public resetUserAccount(id: number): void {
    this.dialog.open(ConfirmComponent, {width: '300px', height: 'auto', maxWidth: '40wv'})
      .afterClosed()
      .pipe(
        filter(v => !!v),
        switchMap(() => this.userService.resetUserAccount(id)))
      .subscribe(() => this.userService.requestUser());
  }

  public rotateProvablyFairSeed(id: number): void {
    this.dialog.open(ConfirmComponent, {width: '300px', height: 'auto', maxWidth: '40wv'})
      .afterClosed()
      .pipe(
        filter(v => !!v),
        tap(() => this.isRotatingProvablyFairSeed = true),
        switchMap(() => this.userService.rotateProvablyFairSeed(id)),
        finalize(() => this.isRotatingProvablyFairSeed = false),
        takeUntil(this.destroyed$),
      )
      .subscribe(result => {
        if (result) {
          this.sb.openSnackBar('Provably-fair seed успешно сменён', 3000, 'success');
        }
      });
  }
}
